import { Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

type ThemePreference = "light" | "dark";

const STORAGE_KEY = "qbank-theme";

const OPTIONS: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
];

function readStored(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // storage unavailable — fall through to the OS preference
  }
  // No stored choice: show the theme the OS is already giving the page, so one icon is lit.
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Stamps `data-theme` on <html> — BRANDING §9's mechanism, index.css does the rest.
 * index.html has a blocking inline script that applies a stored choice before first
 * paint; this mirrors its read/write so the two never disagree.
 */
function applyTheme(preference: ThemePreference) {
  document.documentElement.dataset.theme = preference;
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // storage unavailable — theme still applies for this page load
  }
}

export function ThemeToggle() {
  const [preference, setPreference] = useState<ThemePreference>(readStored);

  useEffect(() => applyTheme(preference), [preference]);

  return (
    <div
      className="bg-bank-surface-3 inline-flex items-center gap-0.5 rounded-md p-0.5"
      role="radiogroup"
      aria-label="Theme"
    >
      {OPTIONS.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={preference === value}
          title={label}
          onClick={() => setPreference(value)}
          className={cn(
            "grid size-6 place-items-center rounded transition-colors",
            preference === value
              ? "bg-bank-surface text-bank-accent shadow-sm"
              : "text-bank-muted hover:text-bank-ink",
          )}
        >
          <Icon className="size-3.5" aria-hidden="true" />
          <span className="sr-only">{label}</span>
        </button>
      ))}
    </div>
  );
}
